import React, { Component } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { toast } from "react-toastify";
import API from "../utils/api";
import { formatDate } from "../utils/dateFormatter";

const CALL_STATUS_LABELS = {
    0: "Pending",
    1: "Phone Not Picked",
    2: "Phone Switched Off",
    3: "Phone Not Reachable",
    5: "No Requirement",
    6: "Callback Later",
    8: "No DL",
    9: "Duplicate",
    11: "Spam",
    13: "Already Done",
    14: "Done",
    15: "Personal Use",
};

const parseLocationOptions = (params) => {
    const locationValues = params.getAll("location");
    if (!locationValues.length) {
        return [];
    }

    let normalizedLocations = locationValues;

    if (locationValues.length === 1) {
        const [singleValue] = locationValues;

        if (singleValue && singleValue.trim().startsWith("[") && singleValue.trim().endsWith("]")) {
            try {
                const parsed = JSON.parse(singleValue);
                if (Array.isArray(parsed)) {
                    normalizedLocations = parsed;
                }
            } catch (e) {
                normalizedLocations = [singleValue];
            }
        }
    }

    return normalizedLocations
        .map((item) => String(item).trim())
        .filter(Boolean)
        .map((item) => ({ label: item, value: item }));
};

class AdminAttemptedReport extends Component {
    state = {
        leads: [],
        count: 0,
        loading: true,
        page: 0,
        pageSize: 50,
        from: "",
        to: "",
        lead_id: "",
        phone: "",
        source: [],
        location: [],
        medicine_type: [],
        service_for: [],
        service_type: "",
        selling_purpose: "",
        user_id: "",
        team_id: "",
        users: [],
        teams: [],
        sourceOptions: [],
        campaignOptions: [],
        medicineTypeOptions: [],
        serviceForOptions: [],
        serviceTypeOptions: [],
        sellingPurposeOptions: [],
    };

    componentDidMount() {
        this.loadFiltersFromUrl();
        this.loadFilters();
    }

    componentDidUpdate(prevProps) {
        // Fetch report when URL changes (from navigation)
        if (prevProps.location.search !== this.props.location.search) {
            this.loadFiltersFromUrl();
        }
    }

    loadFiltersFromUrl = () => {
        const params = new URLSearchParams(this.props.location.search);
        const from = params.get("from") || "";
        const to = params.get("to") || "";
        const lead_id = params.get("lead_id") || "";
        const phone = params.get("phone") || "";
        const source_str = params.get("source") || "";
        const location = parseLocationOptions(params);
        const medicine_type_str = params.get("medicine_type") || "";
        const service_for_str = params.get("service_for") || "";
        const service_type = params.get("service_type") || "";
        const selling_purpose = params.get("selling_purpose") || "";
        const user_id = params.get("user_id") || "";
        const team_id = params.get("team_id") || "";

        const source = source_str
            ? source_str.split(",").map((s) => ({ label: s.trim(), value: s.trim() })).filter((s) => s.value)
            : [];

        // Convert comma-separated strings to arrays
        const medicine_type = medicine_type_str
            ? medicine_type_str.split(",").map(m => ({ label: m.trim(), value: m.trim() }))
            : [];
        const service_for = service_for_str
            ? service_for_str.split(",").map(s => ({ label: s.trim(), value: s.trim() }))
            : [];

        this.setState({
            from,
            to,
            lead_id,
            phone,
            source,
            location,
            medicine_type,
            service_for,
            service_type,
            selling_purpose,
            user_id,
            team_id,
            campaign: "",
            page: 0,
        }, () => {
            this.fetchReport();
        });
    };

    loadFilters = async () => {
        try {
            const [usersRes, constRes, teamsRes] = await Promise.all([
                API.get("/admin/users"),
                API.get("/const-data"),
                API.get("/teams"),
            ]);
            const usersData = usersRes.data.data || usersRes.data;
            const users = Array.isArray(usersData) ? usersData : [];
            const constData = constRes.data.data || constRes.data || {};
            const teamsData = teamsRes.data.data || teamsRes.data;
            const teams = Array.isArray(teamsData) ? teamsData : [];
            this.setState({
                users,
                teams,
                sourceOptions: constData.source || [],
                medicineTypeOptions: constData.medicine_type || [],
                serviceForOptions: constData.service_for || [],
                serviceTypeOptions: constData.enquiry_type || [],
                sellingPurposeOptions: constData.selling_purpose || [],
            });
        } catch (e) {
            console.error("Failed to load filter options", e);
        }
    };

    loadCampaignOptions = async (source) => {
        try {
            if (!source || source.length !== 1) {
                this.setState({ campaignOptions: [], campaign: "" });
                return;
            }
            const res = await API.get("/campaigns?source=" + encodeURIComponent(source[0].value));
            const data = res.data.data || res.data;
            this.setState({ campaignOptions: data || [] });
        } catch (e) {
            console.error("Failed to load campaign options", e);
        }
    };

    loadLocations = async (inputValue) => {
        if (!inputValue || inputValue.length < 2) return [];

        try {
            const res = await API.get("/cities", { params: { q: inputValue } });
            const cities = res.data.data || res.data || [];

            return cities.map((city) => ({
                value: `${city.city}, ${city.state}`,
                label: `${city.city}, ${city.state}`,
                id: city.id,
            }));
        } catch (e) {
            return [];
        }
    };

    fetchReport = async () => {
        this.setState({ loading: true });
        try {
            const {
                page,
                pageSize,
                from,
                to,
                lead_id,
                phone,
                source,
                location,
                medicine_type,
                service_for,
                service_type,
                selling_purpose,
                user_id,
                team_id,
                campaign,
            } = this.state;
            const params = { limit: pageSize, offset: page * pageSize };
            const hasDirectSearch = Boolean(lead_id || phone);

            if (hasDirectSearch) {
                if (lead_id) params.lead_id = lead_id;
                if (phone) params.phone = phone;
            } else {
                if (from) params.from = from;
                if (to) params.to = to;
                if (source && source.length > 0) {
                    params.source = source.map((s) => s.value).join(",");
                }
                if (location && location.length > 0) {
                    params.location = JSON.stringify(location.map((item) => item.value));
                }
                if (medicine_type && medicine_type.length > 0) {
                    params.medicine_type = medicine_type.map(m => m.value).join(",");
                }
                if (service_for && service_for.length > 0) {
                    params.service_for = service_for.map(s => s.value).join(",");
                }
                if (service_type) params.service_type = service_type;
                if (selling_purpose) params.selling_purpose = selling_purpose;
                if (user_id) params.user_id = user_id;
                if (team_id) params.team_id = team_id;
                if (campaign) params.campaign = campaign;
            }

            const res = await API.get("/admin/report/attempted", { params });
            const data = res.data.data || res.data;
            this.setState({
                leads: data.leads || [],
                count: data.count || 0,
                loading: false,
            });
        } catch (e) {
            toast.error("Failed to load report");
            this.setState({ loading: false });
        }
    };

    handleSearch = (e) => {
        e.preventDefault();
        this.setState({ page: 0 }, this.fetchReport);
    };

    handleReset = () => {
        this.setState(
            {
                from: "",
                to: "",
                lead_id: "",
                phone: "",
                source: [],
                location: [],
                medicine_type: [],
                service_for: [],
                service_type: "",
                selling_purpose: "",
                user_id: "",
                team_id: "",
                campaign: "",
                page: 0,
            },
            this.fetchReport
        );
    };

    handlePageChange = (newPage) => {
        this.setState({ page: newPage }, this.fetchReport);
    };

    render() {
        const {
            leads, count, loading, page, pageSize, from, to, lead_id, phone, source, location,
            medicine_type, service_for, service_type, selling_purpose, user_id, team_id, campaign,
            users, teams, sourceOptions, campaignOptions, medicineTypeOptions, serviceForOptions, serviceTypeOptions, sellingPurposeOptions
        } = this.state;
        const totalPages = Math.ceil(count / pageSize);
        const isDirectSearch = Boolean(lead_id || phone);

        return (
            <div>
                <h2 className="page-header">
                    <i className="fas fa-phone-volume"></i> Attempted Leads Report
                </h2>

                {/* Filters */}
                <form onSubmit={this.handleSearch} className="well" style={{ marginBottom: 20, padding: 15 }}>
                    <div className="row">
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>From</label>
                                <input type="date" className="form-control" value={from}
                                    disabled={isDirectSearch}
                                    onChange={(e) => this.setState({ from: e.target.value })} />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>To</label>
                                <input type="date" className="form-control" value={to}
                                    disabled={isDirectSearch}
                                    onChange={(e) => this.setState({ to: e.target.value })} />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Source</label>
                                <Select
                                    isMulti
                                    isClearable
                                    isSearchable
                                    isDisabled={isDirectSearch}
                                    options={sourceOptions.map((s) => ({ label: s, value: s }))}
                                    value={source}
                                    onChange={(selected) => {
                                        this.setState({ source: selected || [], campaign: "" });
                                        this.loadCampaignOptions(selected || []);
                                    }}
                                    placeholder="All sources"
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Campaign</label>
                                <select className="form-control" value={campaign || ""}
                                    disabled={isDirectSearch || source.length !== 1}
                                    onChange={(e) => this.setState({ campaign: e.target.value })}>
                                    <option value="">Campaign</option>
                                    {campaignOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Service Type</label>
                                <select className="form-control" value={service_type}
                                    disabled={isDirectSearch}
                                    onChange={(e) => this.setState({ service_type: e.target.value })}>
                                    <option value="">All Service Types</option>
                                    {serviceTypeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Medicine Type</label>
                                <Select
                                    isMulti
                                    isClearable
                                    isSearchable
                                    isDisabled={isDirectSearch}
                                    options={medicineTypeOptions.map((m) => ({ label: m, value: m }))}
                                    value={medicine_type}
                                    onChange={(selected) => this.setState({ medicine_type: selected || [] })}
                                    placeholder="Select medicine types..."
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Service For</label>
                                <Select
                                    isMulti
                                    isClearable
                                    isSearchable
                                    isDisabled={isDirectSearch}
                                    options={serviceForOptions.map((s) => ({ label: s, value: s }))}
                                    value={service_for}
                                    onChange={(selected) => this.setState({ service_for: selected || [] })}
                                    placeholder="Select services..."
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Selling Purpose</label>
                                <select className="form-control" value={selling_purpose}
                                    disabled={isDirectSearch}
                                    onChange={(e) => this.setState({ selling_purpose: e.target.value })}>
                                    <option value="">All Selling Purposes</option>
                                    {sellingPurposeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="row">

                        <div className="col-md-2">
                            <div className="form-group">
                                <label>Location</label>
                                <AsyncSelect
                                    cacheOptions
                                    defaultOptions={false}
                                    isMulti
                                    isClearable
                                    isDisabled={isDirectSearch}
                                    loadOptions={this.loadLocations}
                                    value={location}
                                    onChange={(selected) => this.setState({ location: selected || [] })}
                                    placeholder="Search locations..."
                                />
                            </div>
                        </div>

                        <div className="col-md-2">
                            <div className="form-group">
                                <label>Executive</label>
                                <select className="form-control" value={user_id}
                                    disabled={isDirectSearch}
                                    onChange={(e) => this.setState({ user_id: e.target.value })}>
                                    <option value="">All Executives</option>
                                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-2">
                            <div className="form-group">
                                <label>Team</label>
                                <select className="form-control" value={team_id}
                                    disabled={isDirectSearch}
                                    onChange={(e) => this.setState({ team_id: e.target.value })}>
                                    <option value="">All Teams</option>
                                    {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="col-md-2">
                            <div className="form-group">
                                <label>Lead ID</label>
                                <input type="text" className="form-control" placeholder="Search by lead id..." value={lead_id}
                                    onChange={(e) => this.setState({ lead_id: e.target.value.replace(/\D/g, "") })} />
                            </div>
                        </div>
                        <div className="col-md-2">
                            <div className="form-group">
                                <label>Phone Number</label>
                                <input type="text" className="form-control" placeholder="Search by phone..." value={phone}
                                    onChange={(e) => this.setState({ phone: e.target.value.replace(/\D/g, "") })} />
                            </div>
                        </div>

                        <div className="col-md-2">
                            <div className="form-group">
                                <label>&nbsp;</label>
                                <div>
                                    <button type="submit" className="btn btn-primary" style={{ marginRight: 5 }}>
                                        <i className="fas fa-search"></i> Search
                                    </button>
                                    <button type="button" className="btn btn-default" onClick={this.handleReset}>
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>

                {/* Results */}
                {loading ? (
                    <p><i className="fas fa-spinner fa-spin"></i> Loading...</p>
                ) : (
                    <div>
                        <p className="text-muted">
                            Showing {Math.min(page * pageSize + 1, count)}–{Math.min((page + 1) * pageSize, count)} of {count} attempted leads
                        </p>
                        <div className="table-responsive">
                            <table className="table table-striped table-bordered table-hover">
                                <thead>
                                    <tr>
                                        <th>Lead ID</th>
                                        <th>Name</th>
                                        <th>Mobile</th>
                                        <th>Email</th>
                                        <th>Source</th>
                                        <th>Service Type</th>
                                        <th>Medicine Type</th>
                                        <th>Executive</th>
                                        <th>Call Status</th>
                                        <th>Remark</th>
                                        <th>Processed At</th>
                                        <th>Recordings</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leads.length === 0 ? (
                                        <tr><td colSpan="13" className="text-center">No leads found</td></tr>
                                    ) : (
                                        leads.map((lead, i) => (
                                            <tr key={`${lead.id}-${i}`}>
                                                <td>{lead.id}</td>
                                                <td>{lead.name}</td>
                                                <td>{lead.mobile}</td>
                                                <td>{lead.email}</td>
                                                <td>{lead.source}</td>
                                                <td>{lead.service_type}</td>
                                                <td>{lead.medicine_type}</td>
                                                <td>{lead.executive_name}</td>
                                                <td>
                                                    <span className="label label-warning">
                                                        {CALL_STATUS_LABELS[lead.call_status] || lead.call_status}
                                                    </span>
                                                </td>
                                                <td>{lead.remark || "-"}</td>
                                                <td>{formatDate(lead.processed_at)}</td>
                                                <td>
                                                    <RecordingCell
                                                        leadId={lead.id}
                                                        allocationId={lead.allocation_id}
                                                        recordingUrls={lead.recording_urls}
                                                    />
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <nav className="text-center">
                                <ul className="pagination">
                                    <li className={page === 0 ? "disabled" : ""}>
                                        <a href="#prev" onClick={(e) => { e.preventDefault(); if (page > 0) this.handlePageChange(page - 1); }}>&laquo;</a>
                                    </li>
                                    {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                                        let p = totalPages <= 10 ? i : Math.max(0, Math.min(page - 4, totalPages - 10)) + i;
                                        return (
                                            <li key={p} className={page === p ? "active" : ""}>
                                                <a href={`#page-${p}`} onClick={(e) => { e.preventDefault(); this.handlePageChange(p); }}>{p + 1}</a>
                                            </li>
                                        );
                                    })}
                                    <li className={page >= totalPages - 1 ? "disabled" : ""}>
                                        <a href="#next" onClick={(e) => { e.preventDefault(); if (page < totalPages - 1) this.handlePageChange(page + 1); }}>&raquo;</a>
                                    </li>
                                </ul>
                            </nav>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

export default AdminAttemptedReport;

const formatTime = (secs) => {
    if (!secs || isNaN(secs)) return "0:00";
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
};

const RecordingCell = ({ leadId, allocationId, recordingUrls }) => {
    const [expanded, setExpanded] = React.useState(false);
    const [presignedUrls, setPresignedUrls] = React.useState({});
    const [loading, setLoading] = React.useState({});
    const [playingKey, setPlayingKey] = React.useState(null);
    const [paused, setPaused] = React.useState(false);
    const [currentTime, setCurrentTime] = React.useState(0);
    const [duration, setDuration] = React.useState(0);
    const audioRef = React.useRef(null);

    let urls = [];
    if (recordingUrls) {
        try {
            urls = typeof recordingUrls === "string" ? JSON.parse(recordingUrls) : recordingUrls;
        } catch (_) {
            urls = [];
        }
    }

    if (!urls || urls.length === 0) return <span className="text-muted">-</span>;

    const fetchPresignedUrl = async (key) => {
        if (presignedUrls[key]) return presignedUrls[key];
        setLoading((prev) => ({ ...prev, [key]: true }));
        try {
            const res = await API.post("/admin/recording/presigned-url", {
                key,
                lead_id: leadId,
                allocation_id: allocationId,
            });
            const url = res.data.data.url;
            setPresignedUrls((prev) => ({ ...prev, [key]: url }));
            return url;
        } catch (e) {
            console.error("Failed to fetch presigned URL for recording", e);
            toast.error("Failed to fetch recording URL");
            return null;
        } finally {
            setLoading((prev) => ({ ...prev, [key]: false }));
        }
    };

    const stopAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        setPlayingKey(null);
        setPaused(false);
        setCurrentTime(0);
        setDuration(0);
    };

    const handlePlay = async (key) => {
        if (playingKey === key) {
            if (paused) {
                audioRef.current.play();
                setPaused(false);
            } else {
                audioRef.current.pause();
                setPaused(true);
            }
            return;
        }

        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        setPlayingKey(null);
        setPaused(false);
        setCurrentTime(0);
        setDuration(0);

        const url = await fetchPresignedUrl(key);
        if (!url) return;

        const audio = new Audio(url);
        audioRef.current = audio;

        audio.addEventListener("loadedmetadata", () => {
            setDuration(audio.duration);
        });
        audio.addEventListener("timeupdate", () => {
            setCurrentTime(audio.currentTime);
        });
        audio.addEventListener("ended", stopAudio);
        audio.addEventListener("error", () => {
            toast.error("Failed to play recording");
            stopAudio();
        });

        setPlayingKey(key);
        setPaused(false);
        audio.play();
    };

    const handleSeek = (e) => {
        if (!audioRef.current || !duration) return;
        const val = Number(e.target.value);
        audioRef.current.currentTime = val;
        setCurrentTime(val);
    };

    const handleDownload = async (key, index) => {
        const url = await fetchPresignedUrl(key);
        if (!url) return;
        const fileName = key.split("/").pop() || `recording_${index + 1}.m4a`;
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    return (
        <div style={{ minWidth: 160 }}>
            <button
                className="btn btn-xs btn-info"
                onClick={() => setExpanded(!expanded)}
                title="Show recordings"
            >
                <i className={`fas fa-microphone${expanded ? "-slash" : ""}`}></i>{" "}
                {urls.length} recording{urls.length !== 1 ? "s" : ""}
            </button>
            {expanded && (
                <div style={{ marginTop: 6 }}>
                    {urls.map((key, index) => {
                        const filename = key.split("/").pop() || `Recording ${index + 1}`;
                        const isLoading = loading[key];
                        const isActive = playingKey === key;
                        const isPlaying = isActive && !paused;
                        return (
                            <div key={key} style={{ marginBottom: 8, borderBottom: "1px solid #eee", paddingBottom: 6 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "nowrap" }}>
                                    <span
                                        style={{ fontSize: 11, color: "#555", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 110 }}
                                        title={filename}
                                    >
                                        {index + 1}. {filename}
                                    </span>
                                    <button
                                        className={`btn btn-xs ${isPlaying ? "btn-warning" : "btn-success"}`}
                                        onClick={() => handlePlay(key)}
                                        disabled={isLoading}
                                        title={isPlaying ? "Pause" : "Play"}
                                    >
                                        {isLoading ? (
                                            <i className="fas fa-spinner fa-spin"></i>
                                        ) : (
                                            <i className={`fas fa-${isPlaying ? "pause" : "play"}`}></i>
                                        )}
                                    </button>
                                    {isActive && (
                                        <button
                                            className="btn btn-xs btn-danger"
                                            onClick={stopAudio}
                                            title="Stop"
                                        >
                                            <i className="fas fa-stop"></i>
                                        </button>
                                    )}
                                    <button
                                        className="btn btn-xs btn-default"
                                        onClick={() => handleDownload(key, index)}
                                        disabled={isLoading}
                                        title="Download"
                                    >
                                        {isLoading ? (
                                            <i className="fas fa-spinner fa-spin"></i>
                                        ) : (
                                            <i className="fas fa-download"></i>
                                        )}
                                    </button>
                                </div>
                                {isActive && (
                                    <div style={{ marginTop: 4 }}>
                                        <input
                                            type="range"
                                            min={0}
                                            max={duration || 0}
                                            step={0.1}
                                            value={currentTime}
                                            onChange={handleSeek}
                                            style={{ width: "100%", cursor: "pointer", accentColor: "#5bc0de" }}
                                        />
                                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#888", marginTop: 1 }}>
                                            <span>{formatTime(currentTime)}</span>
                                            <span>{formatTime(duration)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
